import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Plus,
  Edit,
  Save,
  X,
  Filter,
  Download,
  AlertCircle,
  Upload,
  CheckCircle,
  FileText,
} from "lucide-react";
import Layout from "../component/Layout";
import { db } from "../services/firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import "./ProductConstraints.css";

// Master data imports
import brandData from "./constant/brand.json";
import machineData from "./constant/machine.json";
import toolData from "./constant/tool.json";
import decorData from "./constant/decor.json";

const masterData = {
  BRAND: brandData.BRAND || {},
  MACHINE: machineData.MACHINE || {},
  TOOL: toolData.TOOLS || {},
  DECOR: decorData.DECOR || {},
};

const BrandMachineToolDecorConstraints = () => {
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  // Upload state
  const uploadInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [productSpecs, setProductSpecs] = useState([]);

  const newProductTemplate = {
    segment: "",
    category: "",
    productSource: "",
    productName: "",
    finishType: "",
    specification: "",
    jmiRatePercent: "",
    operatorRatePercent: "",
    maxReadyServeSla: "",
    orderServeSla: "",
  };

  const [newProduct, setNewProduct] = useState(newProductTemplate);

  const [filters, setFilters] = useState({
    segment: "",
    category: "",
    productSource: "",
    productName: "",
    finishType: "",
    specification: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);


  // Extract master data helpers
  const segments = ["BRAND", "MACHINE", "TOOL", "DECOR"];

  const getCategories = (seg) => {
    const s = (seg || "").toUpperCase();
    return s && masterData[s] ? Object.keys(masterData[s]) : [];
  };

  const getProductSources = (seg, cat) => {
    const s = (seg || "").toUpperCase();
    return s && cat && masterData[s]?.[cat]
      ? Object.keys(masterData[s][cat])
      : [];
  };

  const getProductNames = (seg, cat, src) => {
    const s = (seg || "").toUpperCase();
    return s && cat && src && masterData[s]?.[cat]?.[src]
      ? Object.keys(masterData[s][cat][src])
      : [];
  };

  const getProductData = (seg, cat, src, prodName) => {
    const s = (seg || "").toUpperCase();
    return s && cat && src && prodName
      ? masterData[s]?.[cat]?.[src]?.[prodName]
      : null;
  };

  const getFinishTypes = (seg, cat, src, prodName) => {
    const pData = getProductData(seg, cat, src, prodName);
    if (!pData) return [];

    // For BRAND segment, finishTypes is an array
    if (Array.isArray(pData.finishTypes)) {
      return pData.finishTypes;
    }
    
    // For MACHINE, TOOL, DECOR segments, finishTypes is an object with keys
    if (pData.finishTypes && typeof pData.finishTypes === "object") {
      return Object.keys(pData.finishTypes);
    }
    
    return [];
  };

  const getSpecifications = (seg, cat, src, prodName, finishType) => {
    const pData = getProductData(seg, cat, src, prodName);
    if (!pData) return [];

    // For BRAND segment, specifications are in a "specification" array (independent of finishType)
    if (Array.isArray(pData.specification)) {
      return pData.specification;
    }

    // For MACHINE, TOOL, DECOR segments, specifications are nested under finishTypes
    if (pData.finishTypes && typeof pData.finishTypes === "object" && finishType) {
      const specs = pData.finishTypes[finishType];
      if (Array.isArray(specs)) {
        return specs;
      }
    }
    
    return [];
  };

  // Check if combination is valid in master data
  const isValidCombination = (product) => {
    const seg = (product.segment || "").trim().toUpperCase();
    const cat = (product.category || "").trim();
    const src = (product.productSource || "").trim();
    const prodName = (product.productName || "").trim();
    const finish = (product.finishType || "").trim();
    const spec = (product.specification || "").trim();

    if (!seg || !cat || !src || !prodName || !finish || !spec) return false;

    const root = masterData[seg];
    if (!root || !root[cat] || !root[cat][src] || !root[cat][src][prodName])
      return false;

    const productData = root[cat][src][prodName];
    if (!productData.finishTypes) return false;

    // Check if finish type is valid
    let finishValid = false;
    if (Array.isArray(productData.finishTypes)) {
      finishValid = productData.finishTypes.some(f => f.toUpperCase() === finish.toUpperCase());
    } else if (typeof productData.finishTypes === "object") {
      finishValid = Object.keys(productData.finishTypes).some(f => f.toUpperCase() === finish.toUpperCase());
    }

    if (!finishValid) return false;

    // Check if specification is valid
    let specValid = false;
    if (Array.isArray(productData.specification)) {
      // For BRAND: specification is a direct array
      specValid = productData.specification.some(s => s.toUpperCase() === spec.toUpperCase());
    } else if (
      typeof productData.finishTypes === "object" &&
      Array.isArray(productData.finishTypes[finish])
    ) {
      // For MACHINE/TOOL/DECOR: specifications are nested under finishTypes
      specValid = productData.finishTypes[finish].some(s => s.toUpperCase() === spec.toUpperCase());
    }

    return specValid;
  };

  // Duplicate check
  const isDuplicateEntry = (product, list = productSpecs) => {
    const toS = (v) =>
      String(v ?? "")
        .trim()
        .toUpperCase();

    return list.some((spec) => {
      if (spec.id && product.id && spec.id === product.id) return false;

      return (
        toS(spec.segment) === toS(product.segment) &&
        toS(spec.category) === toS(product.category) &&
        toS(spec.productSource) === toS(product.productSource) &&
        toS(spec.productName) === toS(product.productName) &&
        toS(spec.finishType) === toS(product.finishType) &&
        toS(spec.specification) === toS(product.specification)
      );
    });
  };

  // Fetch from Firestore (BRAND, MACHINE, TOOL, DECOR only)
  useEffect(() => {
    const fetchProductSpecs = async () => {
      try {
        const q = query(
          collection(db, "productSpecs"),
          where("segment", "in", [
            "BRAND",
            "MACHINE",
            "TOOL",
            "DECOR",
            "brand",
            "machine",
            "tool",
            "decor",
          ]),
        );
        const querySnapshot = await getDocs(q);
        const specsData = [];
        const toStr = (v) => (v === null || v === undefined ? "" : String(v));

        querySnapshot.forEach((docSnap) => {
          const d = docSnap.data();
          const seg = toStr(d.segment).trim().toUpperCase();
          if (
            seg === "BRAND" ||
            seg === "MACHINE" ||
            seg === "TOOL" ||
            seg === "DECOR"
          ) {
            specsData.push({
              id: docSnap.id,
              ...d,
              segment: seg,
              category: toStr(d.category),
              productSource: toStr(d.productSource),
              productName: toStr(d.productName),
              finishType: toStr(d.finishType),
              specification: toStr(d.specification),
              jmiRatePercent: d.jmiRatePercent ?? "",
              operatorRatePercent: d.operatorRatePercent ?? "",
              maxReadyServeSla: d.maxReadyServeSla ?? "",
              orderServeSla: d.orderServeSla ?? "",
            });
          }
        });

        setProductSpecs(specsData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching brand/machine/tool/decor specs:", err);
        setLoading(false);
      }
    };

    fetchProductSpecs();
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === "segment") {
      setFilters({
        segment: value,
        category: "",
        productSource: "",
        productName: "",
        finishType: "",
        specification: "",
      });
    } else if (name === "category") {
      setFilters((prev) => ({
        ...prev,
        category: value,
        productSource: "",
        productName: "",
        finishType: "",
        specification: "",
      }));
    } else if (name === "productSource") {
      setFilters((prev) => ({
        ...prev,
        productSource: value,
        productName: "",
        finishType: "",
        specification: "",
      }));
    } else if (name === "productName") {
      setFilters((prev) => ({
        ...prev,
        productName: value,
        finishType: "",
        specification: "",
      }));
    } else if (name === "finishType") {
      setFilters((prev) => ({
        ...prev,
        finishType: value,
        specification: "",
      }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      segment: "",
      category: "",
      productSource: "",
      productName: "",
      finishType: "",
      specification: "",
    });
    setCurrentPage(1);
  };

  // Filter specs (BRAND, MACHINE, TOOL, DECOR only)
  const filteredSpecs = useMemo(() => {
    return productSpecs.filter((product) => {
      const toS = (v) =>
        String(v ?? "")
          .trim()
          .toUpperCase();
      const seg = toS(product.segment);
      if (
        seg !== "BRAND" &&
        seg !== "MACHINE" &&
        seg !== "TOOL" &&
        seg !== "DECOR"
      )
        return false;

      const segmentMatch = !filters.segment || seg === toS(filters.segment);
      const categoryMatch =
        !filters.category || toS(product.category) === toS(filters.category);
      const productSourceMatch =
        !filters.productSource ||
        toS(product.productSource) === toS(filters.productSource);
      const productNameMatch =
        !filters.productName ||
        toS(product.productName) === toS(filters.productName);
      const finishTypeMatch =
        !filters.finishType ||
        toS(product.finishType) === toS(filters.finishType);
      const specificationMatch =
        !filters.specification ||
        toS(product.specification) === toS(filters.specification);

      return (
        segmentMatch &&
        categoryMatch &&
        productSourceMatch &&
        productNameMatch &&
        finishTypeMatch &&
        specificationMatch
      );
    });
  }, [productSpecs, filters]);

  // Paginate
  const paginatedSpecs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredSpecs.slice(startIndex, endIndex);
  }, [filteredSpecs, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredSpecs.length / itemsPerPage));
  }, [filteredSpecs.length, itemsPerPage]);

  // KPI calculations
  const kpiData = useMemo(() => {
    const validSpecs = productSpecs.filter((p) => {
      const s = String(p.segment || "")
        .trim()
        .toUpperCase();
      return (
        s === "BRAND" || s === "MACHINE" || s === "TOOL" || s === "DECOR"
      );
    });
    const covered = validSpecs.length;

    const parseNum = (v) => parseFloat(v) || 0;
    let totalJmiRate = 0,
      totalOperatorRate = 0,
      totalSLA = 0,
      slaCount = 0;

    validSpecs.forEach((p) => {
      totalJmiRate += parseNum(p.jmiRatePercent);
      totalOperatorRate += parseNum(p.operatorRatePercent);
      const sla = parseNum(p.orderServeSla);
      if (sla > 0) {
        totalSLA += sla;
        slaCount++;
      }
    });

    return {
      covered,
      avgJmiRate: covered > 0 ? (totalJmiRate / covered).toFixed(2) : "0.00",
      avgOperatorRate: covered > 0 ? (totalOperatorRate / covered).toFixed(2) : "0.00",
      avgSLA: slaCount > 0 ? (totalSLA / slaCount).toFixed(1) : "—",
    };
  }, [productSpecs]);

  // Numeric helpers
  const fmtVal = (v) => {
    if (v === "" || v === null || v === undefined) return "—";
    const n = parseFloat(v);
    if (isNaN(n)) return String(v);
    return n.toLocaleString("en-IN", { maximumFractionDigits: 1 });
  };



  const calculateRates = (product) => {
    return {
      jmiRate: fmtVal(product.jmiRatePercent),
      operatorRate: fmtVal(product.operatorRatePercent),
    };
  };

  // Add Product handler
  const handleAddProduct = async (e) => {
    e.preventDefault();

    const numField = (v) =>
      v === "" || v === null || v === undefined ? "" : Number(v);

    const normalized = {
      ...newProduct,
      segment: (newProduct.segment || "").trim().toUpperCase(),
      category: (newProduct.category || "").trim(),
      productSource: (newProduct.productSource || "").trim(),
      productName: (newProduct.productName || "").trim(),
      finishType: (newProduct.finishType || "").trim(),
      specification: (newProduct.specification || "").trim(),
      jmiRatePercent: numField(newProduct.jmiRatePercent),
      operatorRatePercent: numField(newProduct.operatorRatePercent),
      maxReadyServeSla: numField(newProduct.maxReadyServeSla),
      orderServeSla: numField(newProduct.orderServeSla),
    };

    if (
      normalized.segment !== "BRAND" &&
      normalized.segment !== "MACHINE" &&
      normalized.segment !== "TOOL" &&
      normalized.segment !== "DECOR"
    ) {
      setError("Only BRAND, MACHINE, TOOL, and DECOR segments are allowed.");
      return;
    }

    if (!isValidCombination(normalized)) {
      setError("Selected product combination is not allowed in master data.");
      return;
    }

    if (isDuplicateEntry(normalized)) {
      setError("A product with these specifications already exists!");
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "productSpecs"), normalized);
      setProductSpecs((prev) => [...prev, { ...normalized, id: docRef.id }]);
      setShowAddProduct(false);
      setNewProduct(newProductTemplate);
      setError("");
    } catch (err) {
      console.error("Error adding product:", err);
      setError("Failed to add product. Please try again.");
    }
  };

  // Handle Edit Product
  const handleEdit = (product) => {
    setEditingProduct({
      ...product,
      segment: product.segment || "",
      category: product.category || "",
      productSource: product.productSource || "",
      productName: product.productName || "",
      finishType: product.finishType || "",
      specification: product.specification || "",
      jmiRatePercent: product.jmiRatePercent ?? "",
      operatorRatePercent: product.operatorRatePercent ?? "",
      maxReadyServeSla: product.maxReadyServeSla ?? "",
      orderServeSla: product.orderServeSla ?? "",
    });
    setError("");
  };

  // Save Edit handler
  const handleSave = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const numField = (v) =>
        v === "" || v === null || v === undefined ? "" : Number(v);

      const normalized = {
        ...editingProduct,
        segment: (editingProduct.segment || "").trim().toUpperCase(),
        category: (editingProduct.category || "").trim(),
        productSource: (editingProduct.productSource || "").trim(),
        productName: (editingProduct.productName || "").trim(),
        finishType: (editingProduct.finishType || "").trim(),
        specification: (editingProduct.specification || "").trim(),
        jmiRatePercent: numField(editingProduct.jmiRatePercent),
        operatorRatePercent: numField(editingProduct.operatorRatePercent),
        maxReadyServeSla: numField(editingProduct.maxReadyServeSla),
        orderServeSla: numField(editingProduct.orderServeSla),
      };

      if (
        normalized.segment !== "BRAND" &&
        normalized.segment !== "MACHINE" &&
        normalized.segment !== "TOOL" &&
        normalized.segment !== "DECOR"
      ) {
        setError("Only BRAND, MACHINE, TOOL, and DECOR segments are allowed.");
        setAdding(false);
        return;
      }

      if (!isValidCombination(normalized)) {
        setError("Selected product combination is not allowed in master data.");
        setAdding(false);
        return;
      }

      if (isDuplicateEntry(normalized)) {
        setError("Another product with these specifications already exists!");
        setAdding(false);
        return;
      }

      const docRef = doc(db, "productSpecs", editingProduct.id);
      await updateDoc(docRef, normalized);
      setProductSpecs((prev) =>
        prev.map((p) => (p.id === editingProduct.id ? normalized : p)),
      );
      setEditingProduct(null);
      setError("");
    } catch (err) {
      console.error("Error updating product:", err);
      setError("Failed to update product. Please try again.");
    }
    setAdding(false);
  };

  // Export to CSV
  const downloadExcel = () => {
    let csvContent =
      "Segment,Category,Product Source,Product Name,Finish Type,Specification,";
    csvContent +=
      "JMI Rate (%),Operator Rate (%),Ready Serve SLA,Order Serve SLA\n";

    filteredSpecs.forEach((product) => {
      const rates = calculateRates(product);
      csvContent += `${product.segment || ""},${product.category || ""},${product.productSource || ""},`;
      csvContent += `${product.productName || ""},${product.finishType || ""},${product.specification || ""},`;
      csvContent += `${rates.jmiRate},${rates.operatorRate},`;
      csvContent += `${product.maxReadyServeSla || ""},${product.orderServeSla || ""}\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "brand_machine_tool_decor_constraints.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download blank template
  const downloadTemplate = async () => {
    const XLSX = await import("xlsx");
    const headers = [
      "segment",
      "category",
      "productSource",
      "productName",
      "finishType",
      "specification",
      "jmiRatePercent",
      "operatorRatePercent",
      "maxReadyServeSla",
      "orderServeSla",
    ];

    const sampleRows = [
      headers,
      [
        "BRAND",
        "BOX",
        "Premium",
        "Ring Box",
        "Plastic",
        "Hinged",
        5,
        5,
        2,
        7,
      ],
      [
        "MACHINE",
        "OUTLET",
        "INDIAN",
        "GOLD TESTING",
        "LIGHT DUTY",
        "1-2(kW)",
        5,
        5,
        3,
        10,
      ],
      [
        "TOOL",
        "STEEL",
        "IMPORT",
        "MINI PLIERS",
        "ROUND NOSE",
        "Without grip",
        5,
        5,
        2,
        5,
      ],
      [
        "DECOR",
        "Goldplated",
        "MINI FRAME",
        "YANTRAS",
        "Classic",
        "Single-Tone",
        5,
        5,
        2,
        7,
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(sampleRows);
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 4, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "BrandMachineToolDecorConstraints",
    );
    XLSX.writeFile(
      wb,
      "brand_machine_tool_decor_constraints_template.xlsx",
    );
  };

  // Bulk Excel upload handler
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const XLSX = await import("xlsx");
    setUploading(true);

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      if (!rows.length) {
        setUploadReport({
          added: 0,
          duplicates: 0,
          invalid: [{ row: "—", reason: "File is empty or has no data rows." }],
          errors: [],
        });
        setShowUploadModal(true);
        setUploading(false);
        return;
      }

      const existingKeys = new Set(
        productSpecs
          .filter((p) => {
            const s = (p.segment || "").trim().toUpperCase();
            return (
              s === "BRAND" ||
              s === "MACHINE" ||
              s === "TOOL" ||
              s === "DECOR"
            );
          })
          .map((p) =>
            `${p.segment}||${p.category}||${p.productSource}||${p.productName}||${p.finishType}||${p.specification}`.toUpperCase(),
          ),
      );
      const batchKeys = new Set();

      const report = { added: 0, duplicates: 0, invalid: [], errors: [] };
      const newDocs = [];

      rows.forEach((raw, idx) => {
        const rowNum = idx + 2;

        const val = (k, aliases = []) => {
          if (raw[k] !== undefined && raw[k] !== "") return raw[k];
          for (const a of aliases) {
            if (raw[a] !== undefined && raw[a] !== "") return raw[a];
          }
          return "";
        };

        const segment = String(val("segment", ["Segment", "SEGMENT"]))
          .trim()
          .toUpperCase();
        const category = String(val("category", ["Category", "CATEGORY"])).trim();
        const productSource = String(
          val("productSource", [
            "Product Source",
            "product_source",
            "PRODUCT_SOURCE",
          ]),
        ).trim();
        const productName = String(
          val("productName", ["Product Name", "product_name", "PRODUCT_NAME"]),
        ).trim();
        const finishType = String(
          val("finishType", ["Finish Type", "finish_type", "FINISH_TYPE"]),
        ).trim();
        const specification = String(
          val("specification", ["Specification", "SPECIFICATION"]),
        ).trim();

        if (
          !segment ||
          !category ||
          !productSource ||
          !productName ||
          !finishType ||
          !specification
        ) {
          report.invalid.push({
            row: rowNum,
            reason: `Missing identity fields (segment, category, productSource, productName, finishType, specification required).`,
          });
          return;
        }

        if (
          segment !== "BRAND" &&
          segment !== "MACHINE" &&
          segment !== "TOOL" &&
          segment !== "DECOR"
        ) {
          report.invalid.push({
            row: rowNum,
            reason: `Segment '${segment}' is not allowed. Only BRAND, MACHINE, TOOL, and DECOR are supported.`,
          });
          return;
        }

        const candidate = {
          segment,
          category,
          productSource,
          productName,
          finishType,
          specification,
        };

        if (!isValidCombination(candidate)) {
          report.invalid.push({
            row: rowNum,
            reason: `Combination not found in master data (${segment} › ${category} › ${productSource} › ${productName} › ${finishType} › ${specification})`,
          });
          return;
        }

        const comboKey = `${segment}||${category}||${productSource}||${productName}||${finishType}||${specification}`.toUpperCase();
        if (existingKeys.has(comboKey) || batchKeys.has(comboKey)) {
          report.duplicates++;
          return;
        }
        batchKeys.add(comboKey);

        const numField = (v) => parseFloat(v) || 0;
        newDocs.push({
          ...candidate,
          jmiRatePercent: numField(
            val("jmiRatePercent", [
              "jmiRate",
              "JMI Rate",
              "JMI Rate %",
            ]),
          ),
          operatorRatePercent: numField(
            val("operatorRatePercent", [
              "operatorRate",
              "Operator Rate",
              "Operator Rate %",
            ]),
          ),
          maxReadyServeSla: numField(
            val("maxReadyServeSla", ["Ready Serve SLA"]),
          ),
          orderServeSla: numField(
            val("orderServeSla", ["Order Serve SLA"]),
          ),
        });
      });

      const addedDocs = [];
      for (const data of newDocs) {
        try {
          const ref = await addDoc(collection(db, "productSpecs"), data);
          addedDocs.push({ ...data, id: ref.id });
          report.added++;
        } catch (err) {
          report.errors.push(err.message);
        }
      }

      if (addedDocs.length) {
        setProductSpecs((prev) => [...prev, ...addedDocs]);
      }

      setUploadReport(report);
      setShowUploadModal(true);
    } catch (err) {
      setUploadReport({
        added: 0,
        duplicates: 0,
        invalid: [],
        errors: [`Failed to parse file: ${err.message}`],
      });
      setShowUploadModal(true);
    }
    setUploading(false);
  };

  // Render Add / Edit Product Form
  const renderProductForm = (
    product,
    setProduct,
    onSubmit,
    isEditing = false,
  ) => {
    const availableCategories = getCategories(product.segment);
    const availableSources = getProductSources(
      product.segment,
      product.category,
    );
    const availableProducts = getProductNames(
      product.segment,
      product.category,
      product.productSource,
    );
    const availableFinishes = getFinishTypes(
      product.segment,
      product.category,
      product.productSource,
      product.productName,
    );
    const availableSpecifications = getSpecifications(
      product.segment,
      product.category,
      product.productSource,
      product.productName,
      product.finishType,
    );

    return (
      <form onSubmit={onSubmit} className="product-form">
        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="form-section">
          <h4>Segment Details</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Segment</label>
              <select
                value={product.segment}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    segment: e.target.value,
                    category: "",
                  }))
                }
                required
              >
                <option value="">Select Segment</option>
                {segments.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Category</label>
              <select
                value={product.category}
                onChange={(e) =>
                  setProduct((prev) => ({ ...prev, category: e.target.value }))
                }
                required
                disabled={!product.segment}
              >
                <option value="">Select Category</option>
                {availableCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Product Details</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Product Source</label>
              <select
                value={product.productSource}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    productSource: e.target.value,
                    productName: "",
                    finishType: "",
                    specification: "",
                  }))
                }
                required
                disabled={!product.category}
              >
                <option value="">Select Source</option>
                {availableSources.map((src) => (
                  <option key={src} value={src}>
                    {src}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Product Name</label>
              <select
                value={product.productName}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    productName: e.target.value,
                    finishType: "",
                    specification: "",
                  }))
                }
                required
                disabled={!product.productSource}
              >
                <option value="">Select Product</option>
                {availableProducts.map((prod) => (
                  <option key={prod} value={prod}>
                    {prod}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Finish Type</label>
              <select
                value={product.finishType}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    finishType: e.target.value,
                    specification: "",
                  }))
                }
                required
                disabled={!product.productName}
              >
                <option value="">Select Finish Type</option>
                {availableFinishes.map((finish) => (
                  <option key={finish} value={finish}>
                    {finish}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Specification</label>
              <select
                value={product.specification}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    specification: e.target.value,
                  }))
                }
                required
                disabled={!product.finishType}
              >
                <option value="">Select Specification</option>
                {availableSpecifications.map((spec) => (
                  <option key={spec} value={spec}>
                    {spec}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Rate Configuration</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>JMI Rate (%)</label>
              <input
                type="number"
                step="0.01"
                value={product.jmiRatePercent}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    jmiRatePercent: e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-field">
              <label>Operator Rate (%)</label>
              <input
                type="number"
                step="0.01"
                value={product.operatorRatePercent}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    operatorRatePercent: e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-field">
              <label>Ready Serve SLA (days)</label>
              <input
                type="number"
                step="1"
                value={product.maxReadyServeSla}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    maxReadyServeSla: e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-field">
              <label>Order Serve SLA (days)</label>
              <input
                type="number"
                step="1"
                value={product.orderServeSla}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    orderServeSla: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={adding} className="btn-primary">
            <Save size={16} />
            {isEditing ? "Update Product" : "Add Product"}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingProduct(null);
              setShowAddProduct(false);
              setError("");
            }}
            className="btn-secondary"
          >
            <X size={16} />
            Cancel
          </button>
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <Layout pageTitle="Brand, Machine, Tool & Decor Constraints">
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Brand, Machine, Tool & Decor Constraints">
      <div className="product-constraints-container">
        <h1>Brand, Machine, Tool & Decor Price Constraints</h1>
        <p>Manage pricing constraints for BRAND, MACHINE, TOOL, and DECOR segments.</p>

        {/* ── KPI Cards Grid ── */}
        <div className="pc-kpi-grid">
          <div className="pc-kpi-card pc-kpi-total">
            <div className="pc-kpi-icon">📦</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? "—" : kpiData.covered.toLocaleString()}</div>
              <div className="pc-kpi-label">Configured</div>
              <div className="pc-kpi-hint">Saved product specs</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-wastage">
            <div className="pc-kpi-icon">💰</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? "—" : `${kpiData.avgJmiRate}%`}</div>
              <div className="pc-kpi-label">Avg JMI Rate</div>
              <div className="pc-kpi-hint">Average JMI percentage</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-mc">
            <div className="pc-kpi-icon">🔧</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? "—" : `${kpiData.avgOperatorRate}%`}</div>
              <div className="pc-kpi-label">Avg Operator Rate</div>
              <div className="pc-kpi-hint">Average operator percentage</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-sla">
            <div className="pc-kpi-icon">⏱️</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? "—" : kpiData.avgSLA === "—" ? "—" : `${kpiData.avgSLA}d`}</div>
              <div className="pc-kpi-label">Avg Order SLA</div>
              <div className="pc-kpi-hint">Average serve days</div>
            </div>
          </div>
        </div>

        <div className="content-wrapper">
          {/* Hidden file input for Excel upload */}
          <input
            ref={uploadInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={handleExcelUpload}
          />

          <div className="header-section">
            <h2>Product Specifications</h2>
            <div className="header-actions">
              <button onClick={downloadTemplate} className="btn-secondary download-btn" title="Download blank template">
                <FileText size={16} /> Template
              </button>
              <button onClick={downloadExcel} className="btn-secondary download-btn">
                <Download size={16} /> Export
              </button>
              <button
                onClick={() => uploadInputRef.current?.click()}
                disabled={uploading}
                className="btn-upload"
              >
                <Upload size={16} />
                {uploading ? "Uploading…" : "Upload Excel"}
              </button>
              <button
                onClick={() => {
                  setShowAddProduct(true);
                  setError("");
                }}
                className="btn-primary add-product-btn"
              >
                <Plus size={16} /> Add Product
              </button>
            </div>
          </div>

          {/* Upload Report Modal */}
          {showUploadModal && uploadReport && (
            <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
              <div className="modal-content upload-report-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>📥 Upload Report</h3>
                  <button onClick={() => setShowUploadModal(false)} className="modal-close">
                    <X />
                  </button>
                </div>

                {/* Summary badges */}
                <div className="upload-report-summary">
                  <div className="ur-badge ur-badge-added">
                    <CheckCircle size={20} />
                    <div>
                      <div className="ur-num">{uploadReport.added}</div>
                      <div className="ur-lbl">Added</div>
                    </div>
                  </div>
                  <div className="ur-badge ur-badge-dup">
                    <AlertCircle size={20} />
                    <div>
                      <div className="ur-num">{uploadReport.duplicates}</div>
                      <div className="ur-lbl">Duplicates Skipped</div>
                    </div>
                  </div>
                  <div className="ur-badge ur-badge-inv">
                    <X size={20} />
                    <div>
                      <div className="ur-num">{uploadReport.invalid.length}</div>
                      <div className="ur-lbl">Invalid Rows</div>
                    </div>
                  </div>
                  {uploadReport.errors.length > 0 && (
                    <div className="ur-badge ur-badge-err">
                      <AlertCircle size={20} />
                      <div>
                        <div className="ur-num">{uploadReport.errors.length}</div>
                        <div className="ur-lbl">Write Errors</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Invalid row details */}
                {uploadReport.invalid.length > 0 && (
                  <div className="upload-report-section">
                    <h4>⚠️ Invalid Rows</h4>
                    <div className="upload-report-table-wrap">
                      <table className="upload-report-table">
                        <thead>
                          <tr>
                            <th>Row #</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadReport.invalid.map((item, i) => (
                            <tr key={i}>
                              <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{item.row}</td>
                              <td style={{ fontSize: "0.8rem", color: "#dc2626" }}>{item.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Write errors */}
                {uploadReport.errors.length > 0 && (
                  <div className="upload-report-section">
                    <h4>🔥 Firestore Errors</h4>
                    <ul style={{ fontSize: "0.82rem", color: "#dc2626", paddingLeft: "1rem" }}>
                      {uploadReport.errors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <button onClick={() => setShowUploadModal(false)} className="btn-primary" style={{ marginTop: "1rem" }}>
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Filters Section */}
          <div className="filters-section">
            <div className="filters-header">
              <Filter size={20} />
              <h3>Filters</h3>
            </div>
            <div className="filters-grid">
              <div className="filter-field">
                <label>Segment</label>
                <select
                  name="segment"
                  value={filters.segment}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {segments.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-field">
                <label>Category</label>
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  disabled={!filters.segment}
                >
                  <option value="">All</option>
                  {filters.segment &&
                    getCategories(filters.segment).map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                </select>
              </div>
              <div className="filter-field">
                <label>Product Source</label>
                <select
                  name="productSource"
                  value={filters.productSource}
                  onChange={handleFilterChange}
                  disabled={!filters.category}
                >
                  <option value="">All</option>
                  {filters.segment &&
                    filters.category &&
                    getProductSources(filters.segment, filters.category).map(
                      (src) => (
                        <option key={src} value={src}>
                          {src}
                        </option>
                      ),
                    )}
                </select>
              </div>
              <div className="filter-field">
                <label>Product Name</label>
                <select
                  name="productName"
                  value={filters.productName}
                  onChange={handleFilterChange}
                  disabled={!filters.productSource}
                >
                  <option value="">All</option>
                  {filters.segment &&
                    filters.category &&
                    filters.productSource &&
                    getProductNames(
                      filters.segment,
                      filters.category,
                      filters.productSource,
                    ).map((prod) => (
                      <option key={prod} value={prod}>
                        {prod}
                      </option>
                    ))}
                </select>
              </div>
              <div className="filter-field">
                <label>Finish Type</label>
                <select
                  name="finishType"
                  value={filters.finishType}
                  onChange={handleFilterChange}
                  disabled={!filters.productName}
                >
                  <option value="">All</option>
                  {filters.segment &&
                    filters.category &&
                    filters.productSource &&
                    filters.productName &&
                    getFinishTypes(
                      filters.segment,
                      filters.category,
                      filters.productSource,
                      filters.productName,
                    ).map((finish) => (
                      <option key={finish} value={finish}>
                        {finish}
                      </option>
                    ))}
                </select>
              </div>
              <div className="filter-field">
                <label>Specification</label>
                <select
                  name="specification"
                  value={filters.specification}
                  onChange={handleFilterChange}
                  disabled={!filters.finishType}
                >
                  <option value="">All</option>
                  {filters.segment &&
                    filters.category &&
                    filters.productSource &&
                    filters.productName &&
                    filters.finishType &&
                    getSpecifications(
                      filters.segment,
                      filters.category,
                      filters.productSource,
                      filters.productName,
                      filters.finishType,
                    ).map((spec) => (
                      <option key={spec} value={spec}>
                        {spec}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="filters-actions">
              <button onClick={clearFilters} className="btn-secondary">
                Clear Filters
              </button>
            </div>
          </div>

          {/* Add / Edit Product Modal */}
          {(showAddProduct || editingProduct) && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>{editingProduct ? "Edit Product Constraint" : "Add New Product Constraint"}</h2>
              {renderProductForm(
                editingProduct || newProduct,
                editingProduct
                  ? setEditingProduct
                  : setNewProduct,
                editingProduct ? handleSave : handleAddProduct,
                !!editingProduct,
              )}
            </div>
          </div>
        )}

        {/* Upload Report Modal */}
        {showUploadModal && uploadReport && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Upload Report</h2>
              <div className="upload-report">
                <div className="report-row">
                  <span>✓ Added:</span>
                  <strong>{uploadReport.added}</strong>
                </div>
                <div className="report-row">
                  <span>⚠ Duplicates:</span>
                  <strong>{uploadReport.duplicates}</strong>
                </div>
                <div className="report-row">
                  <span>✗ Invalid:</span>
                  <strong>{uploadReport.invalid.length}</strong>
                </div>
                {uploadReport.invalid.length > 0 && (
                  <div className="invalid-list">
                    <h4>Invalid Rows:</h4>
                    {uploadReport.invalid.map((inv, idx) => (
                      <div key={idx} className="invalid-item">
                        Row {inv.row}: {inv.reason}
                      </div>
                    ))}
                  </div>
                )}
                {uploadReport.errors.length > 0 && (
                  <div className="errors-list">
                    <h4>Errors:</h4>
                    {uploadReport.errors.map((err, idx) => (
                      <div key={idx} className="error-item">
                        {err}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Product Table */}
        <div className="table-container">
          <table className="product-table">
            <thead>
              <tr>
                <th>Segment</th>
                <th>Category</th>
                <th>Source</th>
                <th>Product</th>
                <th>Finish Type</th>
                <th>Specification</th>
                <th>JMI Rate (%)</th>
                <th>Operator Rate (%)</th>
                <th>Ready SLA</th>
                <th>Order SLA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSpecs.length > 0 ? (
                paginatedSpecs.map((product) => {
                  const rates = calculateRates(product);
                  return (
                    <tr key={product.id}>
                      <td>{product.segment}</td>
                      <td>{product.category}</td>
                      <td>{product.productSource}</td>
                      <td>{product.productName}</td>
                      <td>{product.finishType}</td>
                      <td>{product.specification}</td>
                      <td>{rates.jmiRate}</td>
                      <td>{rates.operatorRate}</td>
                      <td>{fmtVal(product.maxReadyServeSla)}</td>
                      <td>{fmtVal(product.orderServeSla)}</td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleEdit(product)}
                          className="btn-icon"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" className="empty-message">
                    No constraints found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
      </div>
    </Layout>
  );
};

export default BrandMachineToolDecorConstraints;
